import styled, { css } from 'styled-components';

import { size } from '../../layout/helpers';
import { ButtonReset } from '../../layout/reset';

export const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

interface ItemProps {
  $tone?: 'success' | 'warning' | 'error';
  $indentEnd?: boolean;
}

export const Item = styled.li<ItemProps>`
  --bg: var(--c-accent-bg);
  --fg: var(--c-text-main);
  --padding-inline: ${size(1.5)};
  --padding-block: ${size(1)};
  --remove-button-size: ${size(2)};

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--padding-inline);
  padding: var(--padding-block) var(--padding-inline);
  background-color: var(--bg);
  color: var(--fg);
  border-radius: var(--border-radius);

  & + & {
    margin-top: ${size(0.5)};
  }

  ${({ $tone }) =>
    $tone === 'success'
      ? css`
          --bg: var(--c-semantic-success-300);
        `
      : $tone === 'warning'
        ? css`
            --bg: var(--c-semantic-warning-300);
          `
        : $tone === 'error'
          ? css`
              --bg: var(--c-semantic-error-300);

              text-decoration: line-through;
            `
          : undefined}

  ${({ $indentEnd }) =>
    $indentEnd &&
    css`
      margin-right: calc(var(--remove-button-size) / 2);
    `}
`;

export const Prefix = styled.div`
  --prefix-gap: ${size(0.5)};

  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: var(--prefix-gap);
  margin-right: calc((var(--padding-inline) - var(--padding-block)) * -1);
`;

export const Handle = styled.div`
  position: relative;
  font-size: ${size(2.5)};
  user-select: none;
  cursor: grab;

  &::before {
    content: '';
    position: absolute;
    top: calc(var(--padding-block) * -1);
    bottom: calc(var(--padding-block) * -1);
    left: calc(var(--padding-inline) * -1);
    right: calc(var(--prefix-gap) * -1);
  }

  &:hover {
    color: var(--c-text-muted);
  }
`;

export const Favorite = styled.div.attrs(() => ({
  role: 'checkbox',
}))``;

export const FavoriteLabel = styled.label`
  position: relative;
  display: block;
  border-radius: 50%;
  font-size: ${size(3)};
  cursor: pointer;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background-color: rgba(var(--c-overlay-rgb), 0.1);
    opacity: 0;
  }

  &:hover {
    &::before {
      opacity: 1;
    }
  }
`;

export const FavoriteInput = styled.input.attrs(() => ({
  type: 'checkbox',
}))`
  position: fixed;
  width: 1px;
  height: 1px;
  clip-path: inset(50%);
  overflow: hidden;
  white-space: nowrap;

  &[disabled] ~ ${FavoriteLabel} {
    opacity: 0.37;
    cursor: not-allowed;
  }

  @supports selector(:focus-visible) {
    &:focus-visible {
      & ~ ${FavoriteLabel} {
        outline: max(2px, 0.08em) solid var(--c-focus);
        outline-offset: max(2px, 0.08em);
      }
    }
  }

  // Safari & IE11
  @supports not selector(:focus-visible) {
    &:focus {
      & ~ ${FavoriteLabel} {
        outline: max(2px, 0.08em) solid var(--c-focus);
        outline-offset: max(2px, 0.08em);
      }
    }
  }
`;

export const Body = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: ${size(0.5)};
  font-size: ${(props) => props.theme.font.size.small};

  ${(props) => props.theme.breakpoint.Lg} {
    flex-direction: row;
    align-items: center;
  }
`;

export const Title = styled.div`
  flex: 1 1 auto;
`;

export const Meta = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: ${size(1)};
`;

export const InfoBtn = styled(ButtonReset)`
  position: relative;
  flex-shrink: 0;
  font-size: ${size(2.5)};
  border-radius: 50%;
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    inset: ${size(-0.25)};
    background-color: rgba(var(--c-overlay-rgb), 0.1);
    border-radius: inherit;
    opacity: 0;
    z-index: -1;
  }

  &:hover {
    &::before {
      opacity: 1;
    }
  }
`;

export const RemoveBtn = styled(ButtonReset)`
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--remove-button-size);
  height: var(--remove-button-size);
  border-radius: calc(var(--remove-button-size) * 0.375);
  background-color: var(--c-semantic-error-500);
  color: var(--c-bg-main);
  margin-left: calc((var(--padding-inline) - var(--padding-block)) * -1);
  margin-right: calc((var(--padding-inline) * -1) - var(--remove-button-size) / 2);
  font-size: ${(props) => props.theme.font.size.xsmall};

  &:hover {
    background: var(--c-semantic-error-700);
  }

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: ${size(4)};
    height: ${size(4)};
  }

  ${InfoBtn} + & {
    margin-left: 0;
  }
`;
